import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Building2, Users, CheckCircle, Ban, UserCheck, Loader2 } from 'lucide-react';
import { getPlatformAnalytics, type PlatformAnalytics } from '../../services/platformAnalyticsService';

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number; tint: string }> = ({ icon, label, value, tint }) => (
  <Card>
    <CardContent className="p-5 flex items-center gap-4">
      <span className={`inline-flex items-center justify-center w-11 h-11 rounded-[12px] ${tint}`}>{icon}</span>
      <div>
        <div className="text-2xl font-bold text-foreground">{value.toLocaleString()}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
    </CardContent>
  </Card>
);

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PlatformAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await getPlatformAnalytics());
      } catch {
        message.error('Could not load analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!data) {
    return <div className="py-16 text-center text-muted-foreground">No analytics available.</div>;
  }

  const { totals, byPlan, recentTenants } = data;
  const maxPlan = Math.max(1, ...byPlan.map((p) => p.count));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 text-foreground">Platform Analytics</h1>
        <p className="text-muted-foreground mt-1">Health and growth across all tenants.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={<Building2 className="w-5 h-5 text-orange-700 dark:text-orange-300" />} label="Tenants" value={totals.tenants} tint="bg-orange-100 dark:bg-orange-500/[0.12]" />
        <Stat icon={<CheckCircle className="w-5 h-5 text-green-700 dark:text-green-300" />} label="Active" value={totals.activeTenants} tint="bg-green-100 dark:bg-green-500/15" />
        <Stat icon={<Ban className="w-5 h-5 text-red-700 dark:text-red-300" />} label="Suspended" value={totals.suspendedTenants} tint="bg-red-100 dark:bg-red-500/15" />
        <Stat icon={<Users className="w-5 h-5 text-blue-700 dark:text-blue-300" />} label="Users" value={totals.users} tint="bg-blue-100 dark:bg-blue-500/15" />
        <Stat icon={<UserCheck className="w-5 h-5 text-emerald-700" />} label="Active users" value={totals.activeUsers} tint="bg-emerald-100" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle>Tenants by plan</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {byPlan.length === 0 ? (
              <div className="py-6 text-center text-muted-foreground">No data.</div>
            ) : (
              byPlan.map((p) => (
                <div key={p.plan}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-foreground font-medium">{cap(p.plan)}</span>
                    <span className="text-muted-foreground">{p.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: `${(p.count / maxPlan) * 100}%` }} />
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Newest tenants</CardTitle></CardHeader>
          <CardContent className="divide-y divide-border">
            {recentTenants.length === 0 ? (
              <div className="py-6 text-center text-muted-foreground">No tenants yet.</div>
            ) : (
              recentTenants.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`/tenants/${t.id}`)}
                  className="w-full py-2.5 flex items-center gap-3 text-left"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">
                    <Building2 className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground">/{t.slug}</div>
                  </div>
                  <Badge className={t.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 dark:bg-red-500/15 text-red-700'}>
                    {t.isActive ? 'Active' : 'Suspended'}
                  </Badge>
                  <span className="text-xs text-muted-foreground hidden sm:block w-20 text-right">{fmtDate(t.createdAt)}</span>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsPage;
